/**
 * Self-contained player-create dialog.
 *
 * Usage:
 *   <PlayerCreateDialog
 *     open={open} onOpenChange={setOpen}
 *     onCreated={(player) => refreshRoster()}
 *     lockedTeamId={team.id}            // optional — when set, the team picker
 *     lockedTeamName={team.name}        //   is hidden and the new player is
 *                                       //   created with team_ids=[lockedTeamId].
 *   />
 *
 * Fields, validation rules and POST payload mirror the existing create form on
 * /admin/players (Phase 17.1). All helpers live in this file to avoid coupling
 * with PlayersPage's internal subcomponents — kept intentionally; spec
 * explicitly permits duplication.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { X } from "lucide-react";

// ---- Option constants (mirrors PlayersPage.jsx) ---------------------------
const FOOT_OPTIONS = [
  { value: "right", label: "Right" },
  { value: "left",  label: "Left"  },
  { value: "both",  label: "Both"  },
];

const NATIONAL_TEAM_STATUS_OPTIONS = ["Current", "Former"];
const NATIONAL_TEAM_LEVEL_OPTIONS = [
  "U15", "U16", "U17", "U18", "U19", "U20", "U21", "National Team",
];
const PLAYER_POSITIONS = [
  "GK", "RB", "CB", "LB", "DM", "CM", "AM", "RW", "LW", "CF",
];
const NT_EMPTY = "__none__";          // shadcn Select cannot store ""
const POS_EMPTY = "__none__";

const emptyForm = {
  full_name: "",
  date_of_birth: "",
  birth_nationality: "",
  passport_nationalities: [],
  preferred_foot: "right",
  position: "",
  national_team_status: "",
  national_team_level: "",
  height_cm: "",
  weight_kg: "",
  team_ids: [],
};

function validateNationalTeam(status, level) {
  if ((status && !level) || (!status && level)) {
    return "National team status and level must both be set or both empty.";
  }
  return "";
}

// ---- TagInput (passport nationalities) ------------------------------------
function TagInput({ value, onChange, placeholder, testIdPrefix }) {
  const [draft, setDraft] = useState("");
  const addTag = () => {
    const v = draft.trim();
    if (!v) return;
    if (value.includes(v)) { setDraft(""); return; }
    onChange([...value, v]);
    setDraft("");
  };
  return (
    <div data-testid={`${testIdPrefix}-root`}>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); addTag(); }
          }}
          placeholder={placeholder}
          data-testid={`${testIdPrefix}-input`}
        />
        <Button type="button" variant="outline" onClick={addTag} data-testid={`${testIdPrefix}-add`}>
          Add
        </Button>
      </div>
      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-chips`}>
          {value.map((tag) => (
            <Badge key={tag} variant="secondary" className="rounded-none gap-1 pr-1">
              {tag}
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x !== tag))}
                className="inline-flex items-center justify-center rounded-sm hover:bg-muted-foreground/20 p-0.5"
                aria-label="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Position select ------------------------------------------------------
function PositionField({ value, onChange, testIdPrefix }) {
  return (
    <div className="space-y-2">
      <Label>Position <span className="text-muted-foreground font-normal">(optional)</span></Label>
      <Select
        value={value || POS_EMPTY}
        onValueChange={(v) => onChange(v === POS_EMPTY ? "" : v)}
      >
        <SelectTrigger data-testid={`${testIdPrefix}-select`}>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={POS_EMPTY}>— none —</SelectItem>
          {PLAYER_POSITIONS.map((p) => (
            <SelectItem key={p} value={p} data-testid={`${testIdPrefix}-${p}`}>{p}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ---- National team status + level (paired) --------------------------------
function NationalTeamFields({ status, level, onStatusChange, onLevelChange, testIdPrefix }) {
  const mismatch = Boolean(validateNationalTeam(status, level));
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>National team status <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Select
            value={status || NT_EMPTY}
            onValueChange={(v) => onStatusChange(v === NT_EMPTY ? "" : v)}
          >
            <SelectTrigger data-testid={`${testIdPrefix}-status-select`}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NT_EMPTY}>— none —</SelectItem>
              {NATIONAL_TEAM_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o} value={o} data-testid={`${testIdPrefix}-status-${o.toLowerCase()}`}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>National team level <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Select
            value={level || NT_EMPTY}
            onValueChange={(v) => onLevelChange(v === NT_EMPTY ? "" : v)}
          >
            <SelectTrigger data-testid={`${testIdPrefix}-level-select`}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NT_EMPTY}>— none —</SelectItem>
              {NATIONAL_TEAM_LEVEL_OPTIONS.map((o) => (
                <SelectItem key={o} value={o} data-testid={`${testIdPrefix}-level-${o.replace(/\s+/g,'-').toLowerCase()}`}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {mismatch && (
        <p className="text-xs text-rose-500 -mt-2" data-testid={`${testIdPrefix}-error`}>
          Both fields must be set together.
        </p>
      )}
    </>
  );
}

// ---- TwoStepTeamPicker (league → team → chip) -----------------------------
function TwoStepTeamPicker({ teams, leagues, value, onChange, testIdPrefix }) {
  const [leagueId, setLeagueId] = useState("");
  const [teamId, setTeamId] = useState("");
  const teamsForLeague = teams.filter(
    (t) => leagueId && (t.league_ids || []).includes(leagueId) && !value.includes(t.id),
  );
  const teamsById = Object.fromEntries(teams.map((t) => [t.id, t]));

  const addTeam = () => {
    if (!teamId || value.includes(teamId)) return;
    onChange([...value, teamId]);
    setTeamId("");
  };
  const removeTeam = (id) => onChange(value.filter((x) => x !== id));

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-root`}>
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">League</Label>
          <Select value={leagueId} onValueChange={(v) => { setLeagueId(v); setTeamId(""); }}>
            <SelectTrigger data-testid={`${testIdPrefix}-league-select`}>
              <SelectValue placeholder="Pick a league" />
            </SelectTrigger>
            <SelectContent>
              {leagues.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground">No leagues</div>
              ) : leagues.map((l) => (
                <SelectItem key={l.id} value={l.id} data-testid={`${testIdPrefix}-league-option-${l.id}`}>{l.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Team</Label>
          <Select value={teamId} onValueChange={setTeamId} disabled={!leagueId}>
            <SelectTrigger data-testid={`${testIdPrefix}-team-select`}>
              <SelectValue placeholder={leagueId ? (teamsForLeague.length ? "Pick a team" : "No more teams") : "Pick league first"} />
            </SelectTrigger>
            <SelectContent>
              {teamsForLeague.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`${testIdPrefix}-team-option-${t.id}`}>{t.name} ({t.country})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button type="button" onClick={addTeam} disabled={!teamId} data-testid={`${testIdPrefix}-add-button`} className="h-10">
          Add
        </Button>
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-chips`}>
          {value.map((id) => {
            const t = teamsById[id];
            return (
              <Badge key={id} variant="secondary" className="rounded-none gap-1 pr-1" data-testid={`${testIdPrefix}-chip-${id}`}>
                {t ? `${t.name} (${t.country})` : id}
                <button
                  type="button"
                  onClick={() => removeTeam(id)}
                  className="inline-flex items-center justify-center rounded-sm hover:bg-muted-foreground/20 p-0.5"
                  aria-label="Remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- Main dialog ----------------------------------------------------------
export default function PlayerCreateDialog({
  open,
  onOpenChange,
  lockedTeamId = "",
  lockedTeamName = "",
  onCreated,
}) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);

  // Lazy-load teams/leagues the first time the dialog opens without a lock —
  // when the team is locked we don't need the picker at all.
  useEffect(() => {
    if (!open) return;
    if (lockedTeamId) return;
    if (teams.length && leagues.length) return;
    let alive = true;
    setLoadingLists(true);
    Promise.all([api.get("/teams"), api.get("/leagues")])
      .then(([t, l]) => { if (alive) { setTeams(t.data || []); setLeagues(l.data || []); } })
      .catch(() => {})
      .finally(() => { if (alive) setLoadingLists(false); });
    return () => { alive = false; };
  }, [open, lockedTeamId, teams.length, leagues.length]);

  // Reset the form every time the dialog re-opens so prior submission
  // values don't bleed back in.
  useEffect(() => {
    if (open) {
      setForm({
        ...emptyForm,
        team_ids: lockedTeamId ? [lockedTeamId] : [],
      });
    }
  }, [open, lockedTeamId]);

  const titleSuffix = useMemo(
    () => (lockedTeamId && lockedTeamName ? ` · ${lockedTeamName}` : ""),
    [lockedTeamId, lockedTeamName],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ntErr = validateNationalTeam(form.national_team_status, form.national_team_level);
    if (ntErr) { toast.error(ntErr); return; }
    setSaving(true);
    try {
      const team_ids = lockedTeamId ? [lockedTeamId] : form.team_ids;
      const { data } = await api.post("/players", {
        full_name: form.full_name.trim(),
        date_of_birth: form.date_of_birth || null,
        birth_nationality: form.birth_nationality.trim(),
        passport_nationalities: form.passport_nationalities,
        preferred_foot: form.preferred_foot,
        position: form.position.trim() || null,
        national_team_status: form.national_team_status || null,
        national_team_level: form.national_team_level || null,
        height_cm: form.height_cm === "" ? null : parseInt(form.height_cm, 10),
        weight_kg: form.weight_kg === "" ? null : parseInt(form.weight_kg, 10),
        team_ids,
      });
      if (lockedTeamId && lockedTeamName) {
        toast.success(`Player created and added to ${lockedTeamName}`);
      } else {
        toast.success("Player created");
      }
      onOpenChange(false);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="player-create-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">
            New Player{titleSuffix}
          </DialogTitle>
          <DialogDescription>
            {lockedTeamId
              ? `New player will be added to ${lockedTeamName || "the current team"}.`
              : "Profile + optional team assignment. Teams can also be added or changed later from Edit."}
          </DialogDescription>
        </DialogHeader>

        {lockedTeamId && (
          <div
            className="rounded-md bg-muted/50 px-3 py-2 text-xs flex items-center gap-2"
            data-testid="player-create-locked-team"
          >
            <span className="uppercase tracking-[0.18em] text-muted-foreground">Team</span>
            <span className="font-medium text-foreground/85">
              {lockedTeamName || lockedTeamId}
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pcd-name">Full name</Label>
            <Input
              id="pcd-name"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              required
              data-testid="player-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pcd-dob">Date of birth <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                id="pcd-dob"
                type="date"
                value={form.date_of_birth}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                data-testid="player-dob-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pcd-bn">Birth nationality</Label>
              <Input
                id="pcd-bn"
                value={form.birth_nationality}
                onChange={(e) => setForm({ ...form, birth_nationality: e.target.value })}
                required
                data-testid="player-birth-nat-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Passport nationalities <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <TagInput
              value={form.passport_nationalities}
              onChange={(v) => setForm({ ...form, passport_nationalities: v })}
              placeholder="Type and press Enter (e.g. Dutch, Surinamese)"
              testIdPrefix="player-passports"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Preferred foot</Label>
              <Select
                value={form.preferred_foot}
                onValueChange={(v) => setForm({ ...form, preferred_foot: v })}
              >
                <SelectTrigger data-testid="player-foot-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FOOT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`player-foot-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <PositionField
              value={form.position}
              onChange={(v) => setForm({ ...form, position: v })}
              testIdPrefix="player-position"
            />
          </div>
          <NationalTeamFields
            status={form.national_team_status}
            level={form.national_team_level}
            onStatusChange={(v) => setForm({ ...form, national_team_status: v })}
            onLevelChange={(v) => setForm({ ...form, national_team_level: v })}
            testIdPrefix="player-nt"
          />
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pcd-h">Height (cm) <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                id="pcd-h"
                type="number"
                min="1"
                value={form.height_cm}
                onChange={(e) => setForm({ ...form, height_cm: e.target.value })}
                data-testid="player-height-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pcd-w">Weight (kg) <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                id="pcd-w"
                type="number"
                min="1"
                value={form.weight_kg}
                onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
                data-testid="player-weight-input"
              />
            </div>
          </div>
          {/* TwoStepTeamPicker is hidden when the team is locked by the caller. */}
          {!lockedTeamId && (
            <div className="space-y-2">
              <Label>Teams <span className="text-muted-foreground font-normal">(optional)</span></Label>
              {loadingLists ? (
                <div className="text-xs text-muted-foreground">Loading teams…</div>
              ) : (
                <TwoStepTeamPicker
                  teams={teams}
                  leagues={leagues}
                  value={form.team_ids}
                  onChange={(v) => setForm({ ...form, team_ids: v })}
                  testIdPrefix="player-teams"
                />
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="player-create-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="player-create-submit"
            >
              {saving ? "Creating…" : "Create player"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
